import _ from 'lodash';
import stable from 'stable';

import type { UserRole } from '@appTypes/esb';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { RestrictedSBPositions } from '@modules/permissionManager/constants';
import {
  programByProgramId,
  programForUnitTypeId,
  programTypeNameForProgramTypeId,
  unitTypeIdForProgramTypeId,
} from '@modules/shared/constants';
import removeLeadingZeros from '@modules/utils/removeLeadingZeros';
import type { NormalizedOrgProfile } from '@unit/types';

import { key3Roles, organizationRoles } from './constants';
import {
  getCouncilRights,
  hasAdminAkelaRole,
  hasAdminRights,
  hasAdvancementsCoordinatorRights,
  hasApproverSBRole,
  hasCouncilRights,
  hasEditPaymentLogsPerms,
  hasEditUnitPaymentLogsPerms,
  hasEditYouthProfileRolesPerms,
  hasMBCAndLeaderRights,
  hasMBCRights,
  hasMeritBadgeCounselRights,
  hasParentGuardianRights,
  hasPermissionsManagerRights,
  hasScoutbookRole,
  hasSpecialLoginRole,
  hasYouthMemberRights,
} from './permissionChecks';
import type { DedupedRoleType } from './types';

export function addIsScoutbookRole(
  personGuidRoles: UserRole[],
  userIdRoles: UserRole[] = [],
) {
  const sanitazedPersonGuidRoles = featureFlags.getFlag(
    'SBPD_222_REMOVE_PARTIAL_SB_POSITIONS',
  )
    ? personGuidRoles.filter(item => {
        if (item.sbRoleId) {
          return !RestrictedSBPositions.includes(item.role);
        }

        return item;
      })
    : personGuidRoles;

  return [
    ...sanitazedPersonGuidRoles.map(role => ({
      ...role,
      isScoutbookRole: !!role.sbRoleId,
    })),
    ...userIdRoles.map(uir => ({ ...uir, isScoutbookRole: true })),
  ];
}

/**
 * If any of the roles doesn't have a `programType/programTypeId/acceptGender` set, then it uses
 * the program found on the `unitProfiles`.
 */
export function enhanceWithUnitProfile(
  roles: DedupedRoleType[],
  unitProfiles: NormalizedOrgProfile[],
) {
  // TODO: check if keyBy is the best, we might be getting incorrect programTypes
  const profilesByOrgId = _.keyBy(unitProfiles, 'organizationGuid');
  return roles.map(role => {
    const org = profilesByOrgId[role.organizationGuid];
    if (!org) return role;
    return {
      ...role,
      programType: role.programType || _.get(org, 'charter.programName'),
      programTypeId: role.programTypeId || _.get(org, 'charter.programId'),
      acceptGender: role.acceptGender || org.acceptGender,
    };
  });
}

/**
 *
 * Refactor(extracted): This seems to do the following:
 * - filter "valid" organizations
 * - "Compact" orgs
 */
export function filterValidOrgs(roles: DedupedRoleType[]) {
  const validOrganizations = roles.filter(
    org =>
      hasAdminRights(org) ||
      hasSpecialLoginRole(org) ||
      hasAdvancementsCoordinatorRights(org) ||
      hasCouncilRights(org) ||
      hasMeritBadgeCounselRights(org) ||
      hasYouthMemberRights(org) ||
      hasParentGuardianRights(org),
  );

  const validOrganizationsOrdered = stable(validOrganizations, org =>
    org.role === organizationRoles.ADVANCEMENT_COORDINATOR ? 1 : -1,
  );

  const compactOrganizationsSet = _.uniqWith(
    validOrganizationsOrdered,
    (organization, orgCompare) =>
      organization.organizationGuid === orgCompare.organizationGuid &&
      hasParentGuardianRights(organization) ===
        hasParentGuardianRights(orgCompare),
  );

  return compactOrganizationsSet.map(compactOrganizations => ({
    ...compactOrganizations,
    // TODO: move this to another function
    isCalendarEditor: validOrganizationsOrdered.some(
      ({ role, organizationGuid }) =>
        organizationRoles.CALENDAR_EDITOR.includes(role) &&
        compactOrganizations.organizationGuid === organizationGuid,
    ),
  }));
}

function getProgramTypeId(role: DedupedRoleType) {
  const programTypeId =
    role.programTypeId ||
    programTypeNameForProgramTypeId[
      role.programType as keyof typeof programTypeNameForProgramTypeId
    ];

  return programTypeId;
}
function getUnitTypeId(role: DedupedRoleType) {
  const unitTypeId =
    unitTypeIdForProgramTypeId[
      +role.programTypeId as keyof typeof unitTypeIdForProgramTypeId
    ];
  return unitTypeId;
}
function getProgram(role: DedupedRoleType) {
  const unitTypeId = getUnitTypeId(role);

  if (role.programId) {
    const _programId =
      unitTypeIdForProgramTypeId[
        +role.programId as keyof typeof unitTypeIdForProgramTypeId
      ];
    const program = programByProgramId[_programId];
    if (program) return program;
  }

  // Default behavior use unit type id
  return programForUnitTypeId[unitTypeId];
}

/**
 * This is the role that effectively gets stored with extra properties added
 * during initialization.
 */
export function normalizeUserRole(role: DedupedRoleType, personGuid: string) {
  const {
    organizationName,
    organizationNumber,
    programType,
    isAkelaAdvChair,
    ...rest
  } = role;
  const formattedOrganizationName = removeLeadingZeros(organizationName);
  const formattedOrganizationNumber = removeLeadingZeros(organizationNumber);
  const isKey3 = role.roleTypes.some(({ roleType }) =>
    key3Roles.includes(roleType),
  );
  const { isCouncil, hasCouncilEditRights, hasExtraCouncilRole } =
    getCouncilRights(role);
  const isMeritBadgeCounselor = hasMeritBadgeCounselRights(role);
  const isMBCAndLeader = hasMBCAndLeaderRights(role);
  const isYouthMember = hasYouthMemberRights(role);
  const isParentGuardian = hasParentGuardianRights(role);
  const isAdminAkelaRole = hasAdminAkelaRole(role);
  const isScoutbookRole =
    !isAdminAkelaRole &&
    !isAkelaAdvChair &&
    (hasScoutbookRole(role) || hasAdvancementsCoordinatorRights(role));

  const canAproveAdvancements =
    isScoutbookRole &&
    (hasApproverSBRole(role) || hasAdvancementsCoordinatorRights(role));
  const canSeeMBCs = hasMBCRights(role);
  const canEditYouthProfilePerms = hasEditYouthProfileRolesPerms(role);

  const isCouncilAdmin = hasCouncilRights(role);
  const hasLogInRole = hasSpecialLoginRole(role);
  const canEditPaymentLogs = hasEditPaymentLogsPerms(role);
  const canEditUnitPaymentLogs = hasEditUnitPaymentLogsPerms(role);
  const canEditPermissionsManager = hasPermissionsManagerRights(role);

  const program = getProgram(role);
  const unitTypeId = getUnitTypeId(role);
  const programTypeId = getProgramTypeId(role);
  const allRoles = hasExtraCouncilRole
    ? _.uniq([
        ...role.allRoles.filter(
          roleName => !organizationRoles.EXTRA_COUNCIL_ROLES.includes(roleName),
        ),
        'Council Key 3',
      ])
    : role.allRoles;

  return {
    ...rest,
    allRoles,
    programType,
    unitTypeId,
    programTypeId,
    isKey3,
    isCouncil,
    isCouncilAdmin,
    hasCouncilEditRights,
    isMeritBadgeCounselor,
    isMBCAndLeader,
    isYouthMember,
    isParentGuardian,
    isScoutbookRole,
    canAproveAdvancements,
    canSeeMBCs,
    hasLogInRole,
    canEditPaymentLogs,
    canEditUnitPaymentLogs,
    canEditPermissionsManager,
    canEditYouthProfilePerms,
    personGuid: isYouthMember && personGuid,
    program,
    organizationName: formattedOrganizationName,
    organizationNumber: formattedOrganizationNumber,
  };
}
